import { IDIService } from '../../IDIService';
import { ITypedEvent, TypedEvent } from '../../TypedEvent';
import { DeviceState, IDevice, CommunicationWay, DeviceType, DeviceTaskTypeInfo, DeviceResponseTypeInfo, DeviceTasksInfo, DeviceResponseType, ManuallyPairedDevice, IconType, DeviceTaskType } from './IDevice';
import { DeviceTaskPairExecutionResult, IDevicesService } from './IDevicesService';
import { BatteryDeviceState, BinarySensorStateDeviceState, BlindCalibrationDeviceState, BlindErrorState, BlindErrorDeviceState, BlindOpenCloseTimeDeviceState, BlindPositionDeviceState, BlindRemoteButtonDeviceState, ChannelOnOffDeviceState, ConfigurationDeviceState, CurrentLightThresholdDeviceState, CurrentWindThresholdDeviceState, DoorBellDeviceState, FacadePositionDeviceState, FacadeRemoteButtonDeviceState, FacadeTypeDeviceState, FloodSensorDeviceState, GateControllerHealthDeviceState, GatePositionDeviceState, GatewayPositionDeviceState, HumiditySensorDeviceState, IDeviceState, IDeviceStateData, LightBrightnessDeviceState, LightColorDeviceState, LightRGBWDeviceState, LightWarmthDeviceState, MeasuredBrightnessDeviceState, MeasuredDistanceDeviceState, MeasuredEnergyDeviceState, MeasuredTemperatureDeviceState, MovementSensorDeviceState, OnlineCameraState, OvercurrentProtectionDeviceState, OvercurrentProtectionThresholdDeviceState, PressureSensorDeviceState, ReedStateDeviceState, RemoteButtonDeviceState, SignalStrenghtDeviceState, StateReliability, TamperProtectionDeviceState, VibrationSensorDeviceState, WindSpeedDeviceState, HallState, SceneExecutedState, ChannelHallStateState, ChannelSceneExecutedState, IntercomDeviceState } from './IDeviceState';
import { DeviceChannel } from './DeviceChannel';
import { ChannelConfigurations, IDeviceChannel, Roles, TaskExecution } from './IDeviceChannel';
import { Api } from '../../Api';
import { ExalusConnectionService } from '../ExalusConnectionService';
import { DependencyContainer } from '../../DependencyContainer';
import { DataFrame, Status, Method } from '../../DataFrame';
import { Device } from './Device';
import { ConnectionState, IExalusConnectionService } from '../IExalusConnectionService';
import { DeviceControlFeature, IDeviceTask } from './IDeviceTask';
import { BlindActionEnum, SetBlindPosition, SetTemperature, SetLightBrightnessDynamicly, SetLightBrightness, SetLightColor, SetLightTemperature, SetBlindOpenCloseTime, SetBlindMicroventilation, SetFacadePosition, FacadeActionEnum, GetFacadeType, LightRGBW, TurnOnWithTime, SetGatePositionPrecise, GateActionEnum, SetGatePositionPreciseSecure } from './Tasks/Tasks';
import { DeviceTask } from './Tasks/DeviceTask';
import { ISessionService } from '../Session/ISessionService';
import { SessionService } from '../Session/SessionService';
import { IControllerConfigurationService } from '../Controller/IControllerConfigurationService';
import { ControllerConfigurationService } from '../Controller/ControllerConfigurationService';
import { DeviceTaskExecutionResult } from './TaskExecutionResult';
import { ManuallyPairedDevicesService } from './ManuallyPairedDevicesService';
import { IManuallyPairedDevicesService } from './IManuallyPairedDevicesService';
import { IRemoteStorageService, RemoteStorageDataEntry } from '../IRemoteStorageService';
import { RemoteStorageService } from '../RemoteStorageService';
import { AccessLevel, IUser } from '../Users/IUser';
import { WebApiCacheService } from '../WebApi/WebApiCacheService';
import { IWebApiCacheService } from '../WebApi/IWebApiCacheService';
import { ISequenceInfo } from '../Scenes/Scenes';
import { IScenesService, ScenesServiceErrorCode } from '../Scenes/IScenesService';
import { ScenesService } from '../Scenes/ScenesService';
import { ResponseResult } from '../FieldChangeResult';
import { Helpers, ParseObjToMap } from '../../Helpers';
import { Event } from '../../Event';
import { AsyncSemaphore } from '../../AsyncSemaphore';

/**
 * @type Class
 */
export class DevicesService implements IDIService, IDevicesService {
    private _devices: IDevice[] = [];
    public static readonly ServiceName: string = "DevicesService";
    private _onDevicesTasksChangedEvent: ITypedEvent<DeviceTasksInfo[]> = new TypedEvent<DeviceTasksInfo[]>();
    private _onDeviceFoundEvent: ITypedEvent<IDevice> = new TypedEvent<IDevice>();
    private _onDeviceRegisteredEvent: ITypedEvent<IDevice> = new TypedEvent<IDevice>();
    private _onDeviceRemovedEvent: ITypedEvent<IDevice> = new TypedEvent<IDevice>();
    private _onDeviceFirmwareVersionChangedEvent: ITypedEvent<{ Device: IDevice, FirmwareVersion: string }> = new TypedEvent<{ Device: IDevice, FirmwareVersion: string }>();
    private _onDeviceStateChangedEvent: ITypedEvent<{ Device: IDevice, State: IDeviceState<IDeviceStateData> }> = new TypedEvent<{ Device: IDevice, State: IDeviceState<IDeviceStateData> }>();
    private _onDeviceStateRefreshedOrChangedEvent: ITypedEvent<{ Device: IDevice, State: IDeviceState<IDeviceStateData> }> = new TypedEvent<{ Device: IDevice, State: IDeviceState<IDeviceStateData> }>();
    private _connection: IExalusConnectionService | null = null;
    private _session: ISessionService | null = null;
    private _controllerConfiguration: IControllerConfigurationService | null = null;
    private _synchronized: boolean = false;
    private _synchronizedDevicesStates: boolean = false;
    private _devicesChannelsStates: { DeviceGuid: string, ChannelsStates: DeviceChannelStates[] }[] = [];
    private _onDevicesSynchronized: Event = new Event();
    private _onDevicesStatesSynchronized: Event = new Event();
    private _syncSemaphore: AsyncSemaphore = new AsyncSemaphore(1);

    constructor() {
        this._connection = Api.Get<IExalusConnectionService>(ExalusConnectionService.ServiceName);
        this._session = Api.Get<ISessionService>(SessionService.ServiceName);
        this._controllerConfiguration = Api.Get<IControllerConfigurationService>(ControllerConfigurationService.ServiceName);
        this._onDeviceStateChangedEvent.Subscribe(async (args) => this.PutDeviceStateOnList(args.Device, args.State));

        this._session?.OnUserLoggedInEvent().Subscribe(async user => {
            try {
                DependencyContainer.Log?.Debug(DevicesService.ServiceName, `User logged in: ${user.Guid} syncing devices...`);
                await this.ResyncDevicesAsync();
            }
            catch (ex: any) {
                DependencyContainer.Log?.Error(DevicesService.ServiceName, `Failed to sync devices and states: ${ex}`);
            }
        });

        this._connection.SubscribeTo<string[]>("/info/devices/tasks", (frame) => {
            this.ParseDeviceTaskInfo(frame.Data as string[]);
        });

        this._connection.SubscribeTo<DeviceObject[]>("/info/devices/device/found", (frame) => {
            const foundDevices = this.MapApiDevices(frame.Data as DeviceObject[]);
            foundDevices.forEach(device => {
                if (device.DeviceType == DeviceType.Scene)
                    return;

                DependencyContainer.Log?.Debug(DevicesService.ServiceName, `Found device: ${device.Name} guid: ${device.Guid}`);
                (this._onDeviceFoundEvent as TypedEvent<IDevice>).Invoke(device);
            });
        });

        this._connection.SubscribeTo<any>("/info/devices/device/firmware/updated", (frame) => {
            const deviceGuid = frame.Data.DeviceGuid;
            const version = frame.Data.Version;
            const dev = this._devices.find(a => a.Guid == deviceGuid);

            if (dev !== undefined) {
                (this._onDeviceFirmwareVersionChangedEvent as TypedEvent<{ Device: IDevice, FirmwareVersion: string }>).Invoke({ Device: dev, FirmwareVersion: version });
                (dev as Device).SoftwareVersion = version;
                (dev.OnDeviceFirmwareVersionChangedEvent() as TypedEvent<{ FirmwareVersion: string }>).Invoke({ FirmwareVersion: version });
            }
        });

        this._connection.SubscribeTo<DeviceStateChangedObject>("/info/devices/device/state/changed", (frame) => {
            const state = frame.Data as DeviceStateChangedObject;
            const devices = this._devices.filter(a => a.Guid == state.DeviceGuid);

            if (devices.length > 0) {
                const device = devices[0];

                const st = this.MapApiDeviceStateToDeviceState(device, state);

                if (st != null) {

                    (this._onDeviceStateRefreshedOrChangedEvent as TypedEvent<{ Device: IDevice, State: IDeviceState<IDeviceStateData> }>)
                        .Invoke({ Device: device, State: st });

                    if (st.TypeAsEnum == DeviceResponseType.SignalStrength) {
                        device.Channels.forEach(channel => {
                            /// object copy { ...state }
                            let newState = new SignalStrenghtDeviceState({ ...state });
                            newState.Data.Channel = channel.Number;

                            (this._onDeviceStateRefreshedOrChangedEvent as TypedEvent<{ Device: IDevice, State: IDeviceState<IDeviceStateData> }>)
                                .Invoke({ Device: device, State: newState });
                        });
                    }
                    else if (st.TypeAsEnum == DeviceResponseType.BatteryState) {
                        device.Channels.forEach(channel => {
                            let newState = new BatteryDeviceState({ ...state });
                            newState.Data.Channel = channel.Number;

                            (this._onDeviceStateRefreshedOrChangedEvent as TypedEvent<{ Device: IDevice, State: IDeviceState<IDeviceStateData> }>)
                                .Invoke({ Device: device, State: newState });
                        });
                    }

                    /// react only if state data changes, not just state time!

                    let channelState = device.States.find(a => a.TypeAsEnum === st.TypeAsEnum && a.Data.Channel === st.Data.Channel);
                    if (channelState) {
                        let compData = channelState.StatesComparisonData;

                        if (st.TypeAsEnum == DeviceResponseType.BlindErrorState) {
                            const errCodeNew = (st.Data as BlindErrorState)?.ErrorCode;
                            const errStateCurr = (channelState as IDeviceState<BlindErrorState>).Data.ErrorCode;
                            if (errCodeNew != errStateCurr) {
                                (this._onDeviceStateChangedEvent as TypedEvent<{ Device: IDevice, State: IDeviceState<IDeviceStateData> }>)
                                    .Invoke({ Device: device, State: st });
                                return;
                            }
                        }
                        // replace old state object data with new one
                        channelState.Data = st.Data;
                        // compare old state with new one
                        if (compData !== st.StatesComparisonData) {
                            (this._onDeviceStateChangedEvent as TypedEvent<{ Device: IDevice, State: IDeviceState<IDeviceStateData> }>)
                                .Invoke({ Device: device, State: st });

                            if (st.TypeAsEnum == DeviceResponseType.SignalStrength) {
                                device.Channels.forEach(channel => {
                                    /// object copy { ...state }
                                    let newState = new SignalStrenghtDeviceState({ ...state });
                                    newState.Data.Channel = channel.Number;

                                    (this._onDeviceStateChangedEvent as TypedEvent<{ Device: IDevice, State: IDeviceState<IDeviceStateData> }>)
                                        .Invoke({ Device: device, State: newState });
                                });
                            }

                            else if (st.TypeAsEnum == DeviceResponseType.BatteryState) {
                                device.Channels.forEach(channel => {
                                    let newState = new BatteryDeviceState({ ...state });
                                    newState.Data.Channel = channel.Number;

                                    (this._onDeviceStateChangedEvent as TypedEvent<{ Device: IDevice, State: IDeviceState<IDeviceStateData> }>)
                                        .Invoke({ Device: device, State: newState });
                                });
                            }
                        }
                    }
                    else {
                        device.States.push(st);
                        (this._onDeviceStateChangedEvent as TypedEvent<{ Device: IDevice, State: IDeviceState<IDeviceStateData> }>)
                            .Invoke({ Device: device, State: st });

                        if (st.TypeAsEnum == DeviceResponseType.SignalStrength) {
                            device.Channels.forEach(channel => {
                                /// object copy { ...state }
                                let newState = new SignalStrenghtDeviceState({ ...state });
                                newState.Data.Channel = channel.Number;

                                (this._onDeviceStateChangedEvent as TypedEvent<{ Device: IDevice, State: IDeviceState<IDeviceStateData> }>)
                                    .Invoke({ Device: device, State: newState });
                            });
                        }

                        else if (st.TypeAsEnum == DeviceResponseType.BatteryState) {
                            device.Channels.forEach(channel => {
                                let newState = new BatteryDeviceState({ ...state });
                                newState.Data.Channel = channel.Number;

                                (this._onDeviceStateChangedEvent as TypedEvent<{ Device: IDevice, State: IDeviceState<IDeviceStateData> }>)
                                    .Invoke({ Device: device, State: newState });
                            });
                        }
                    }
                }
            }
        });

        this._connection.SubscribeTo<DeviceObject[]>("/info/devices/devices/registered", (frame) => {
            Api.Get<IWebApiCacheService>(WebApiCacheService.ServiceName).ClearCache();
            const registeredDevices = this.MapApiDevices(frame.Data as DeviceObject[]);
            registeredDevices.forEach(device => {
                DependencyContainer.Log?.Debug(DevicesService.ServiceName, `Registered device: ${device.Name} guid: ${device.Guid}`);
                this._devices.push(device);
                (this._onDeviceRegisteredEvent as TypedEvent<IDevice>).Invoke(device);
            });
        });

        this._connection.SubscribeTo<DeviceObject[]>("/info/devices/devices/removed", (frame) => {
            Api.Get<IWebApiCacheService>(WebApiCacheService.ServiceName).ClearCache();
            const removedDevices = this.MapApiDevices(frame.Data as DeviceObject[]);
            removedDevices.forEach(device => {
                DependencyContainer.Log?.Debug(DevicesService.ServiceName, `Removed device: ${device.Name} guid: ${device.Guid}`);
                this._devices = this._devices.filter(a => a.Guid != device.Guid);
                (this._onDeviceRemovedEvent as TypedEvent<IDevice>).Invoke(device);
            });
        });
    }

    private async ResyncDevicesAsync(): Promise<void> {
        const lock = await this._syncSemaphore.AcquireAsync();

        try {
            this._synchronized = false;
            this._synchronizedDevicesStates = false;
            this._devices = await this.GetPairedDevicesAsync(true);
            this._synchronized = true;
            this._onDevicesSynchronized.Invoke();
            await this.SyncDevicesStatesAsync();

            this._synchronizedDevicesStates = true;
            this._onDevicesStatesSynchronized.Invoke();
        }
        catch (ex) {
            DependencyContainer.Log?.Error(DevicesService.ServiceName, `Failed to sync devices and states: ${ex}`);
        }
        finally {
            lock.Release();
        }
    }

    WaitForSynchronizationAsync(): Promise<void> {
        if (this._synchronized) {
            DependencyContainer.Log?.Debug(DevicesService.ServiceName, 'Already synchronized');
            return Promise.resolve();
        }

        return new Promise<void>((resolve) => {
            const timeoutId = window.setTimeout(() => {
                this._onDevicesSynchronized.Unsubscribe(handler);
                resolve();
            }, 30_000);

            const handler = () => {
                DependencyContainer.Log?.Debug(
                    DevicesService.ServiceName,
                    'Fired onDevicesSynchronized event – clearing timeout & resolving promise'
                );
                window.clearTimeout(timeoutId);
                this._onDevicesSynchronized.Unsubscribe(handler);
                resolve();
            };

            this._onDevicesSynchronized.Subscribe(handler);
        });
    }

    WaitForDevicesStatesSynchronizationAsync(): Promise<void> {
        if (this._synchronizedDevicesStates) {
            DependencyContainer.Log?.Debug(DevicesService.ServiceName, 'Already synchronized states');
            return Promise.resolve();
        }

        return new Promise<void>((resolve) => {
            const timeoutId = window.setTimeout(() => {
                this._onDevicesStatesSynchronized.Unsubscribe(handler);
                resolve();
            }, 120_000);

            const handler = () => {
                DependencyContainer.Log?.Debug(
                    DevicesService.ServiceName,
                    'Fired onDevicesStatesSynchronized event, CLEARING timeout AND resolving WaitForDevicesStatesSynchronizationAsync promise'
                );
                window.clearTimeout(timeoutId);
                this._onDevicesStatesSynchronized.Unsubscribe(handler);
                resolve();
            };

            this._onDevicesStatesSynchronized.Subscribe(handler);
        });
    }

    ProtocolGuidToProtocolName(guid: string): string | null {
        switch (guid) {
            case "1fca7e8a-a2dc-4883-bd94-4e9b2012f685":
                return "ExtaLife";

            case "cd25bdff-628c-4dbc-a16a-648c96a819d4":
                return "Portos 433MHz";

            case "634c2bb2-1a4f-4db6-8dd4-d5fbaf92678e":
                return "Supla";

            case "194aeb81-990c-4a02-9236-59e902958fda":
                return "RTSP";

            case "b3e4644e-ab5b-494e-b3c5-66fabe7fff64":
                return "Wekta";

            case "247198e1-e360-4355-9886-232ac1456eea":
                return "Lavva Wi-Fi";

            default:
                return null;
        }
    }

    CanDisplaySceneDevices(): boolean {
        return this.CheckIfControllerSoftwareVersionIsEnough(6, 56);
    }

    public CheckIfControllerSoftwareVersionIsEnough(requiredContainerMajor: number, requiredContainerMinor: number): boolean {
        const versionString = this._session?.User?.SoftwareVersion ?? null;

        if (!versionString || !versionString.includes('.'))
            return false;

        const segments = versionString.split('.');
        if (segments.length !== 2)
            return false;

        const numbers = segments.map(s => parseInt(s, 10));
        if (numbers.some(n => Number.isNaN(n) || n < 0))
            return false;

        const [containerMajor, containerMinor] = numbers as [number, number];
        return (containerMajor > requiredContainerMajor) || (containerMajor === requiredContainerMajor && containerMinor >= requiredContainerMinor);
    }

    CheckIfDeviceUsedInScenesAsync(deviceGuid: string): Promise<Status | DeviceUsedInScenes>;
    CheckIfDeviceUsedInScenesAsync(device: IDevice): Promise<Status | DeviceUsedInScenes>;
    async CheckIfDeviceUsedInScenesAsync(dev: unknown): Promise<Status | DeviceUsedInScenes> {
        try {
            let device: IDevice;

            if (typeof dev == 'string') {
                const result = this.GetDevice(dev);

                if (result != null)
                    device = result;
                else {
                    DependencyContainer.Log?.Error(`Cannot check if device is used in scenes! Device with given guid not exists`);
                    return Status.ResourceDoesNotExists;
                }
            }
            else {
                device = dev as IDevice;
            }

            const resp = await this._connection?.SendAndWaitForResponseAsync<DeviceUsedInScenesResponse>(new DeviceIsUsedInScenesRequest(device.Guid), 20000, false);

            if (resp == null || resp.Status == null) {
                DependencyContainer.Log?.Error(`Cannot check if device is used in scenes! Response or status is null!`);
                return Status.FatalError;
            }

            if (resp.Status != Status.OK)
                return resp.Status;

            if (resp.Data == null) {
                DependencyContainer.Log?.Error(`Cannot check if device is used in scenes! No data!`);
                return Status.FatalError;
            }

            let seqList = await Api.Get<IScenesService>(ScenesService.ServiceName).GetSequencesListAsync();

            if (seqList instanceof ResponseResult) {
                DependencyContainer.Log?.Error(`Cannot check if device is used in scenes! Cannot get sequence list, ScenesServiceErrorCode: ${(seqList as ResponseResult<ScenesServiceErrorCode>).Type}`);
                return Status.Error;
            }

            const result = new DeviceUsedInScenes();
            result.DeviceGuid = device.Guid;

            resp.Data.ConditionsAsCondition.forEach(cGuid => {
                const con = (seqList as ISequenceInfo[]).find(s => s.Guid == cGuid)
                if (con)
                    result.ConditionsAsCondition.push(con);
            })

            resp.Data.ConditionsAsTask.forEach(tGuid => {
                const con = (seqList as ISequenceInfo[]).find(s => s.Guid == tGuid)
                if (con)
                    result.ConditionsAsCondition.push(con);
            })

            return result;

        } catch (error) {
            DependencyContainer.Log?.Error(`Cannot check if device is used in scenes! ${error}`);
            return Status.FatalError;
        }
    }

    CanEditDevicesInfo(): boolean {
        if (this._session === null || this._session === undefined)
            return false;

        if (this._session.User === null || this._session.User === undefined)
            return false;

        return this._session?.User?.AccessLevel >= AccessLevel.Admin;
    }

    CanAddAndRemoveAndConfigureDevices(): boolean {
        if (this._session === null || this._session === undefined)
            return false;

        if (this._session.User === null || this._session.User === undefined)
            return false;

        return this._session?.User?.AccessLevel >= AccessLevel.Installator;
    }

    GetDeviceChannelByChannelId(channelId: string): IDeviceChannel | null {
        return (this._devices.firstOrDefault(device => device.Channels.any(channel => channel.ChannelId == channelId)) as IDevice)
            .Channels.firstOrDefault(channel => channel.ChannelId == channelId);
    }

    async EnableFastStatesSyncAsync(): Promise<Status> {
        const res = Api.Get<IRemoteStorageService>(RemoteStorageService.ServiceName).SaveAsync("FastDevicesSync", false, true);
        DependencyContainer.Log?.Error(DevicesService.ServiceName, `EXIT CONFIGURATION MODE WILL BE FIRED! EnableFastStatesSyncAsync()`);
        await Api.Get<IControllerConfigurationService>(ControllerConfigurationService.ServiceName).ExitConfigurationModeAsync();
        return res;
    }

    async DisableFastStatesSyncAsync(): Promise<Status> {
        const res = Api.Get<IRemoteStorageService>(RemoteStorageService.ServiceName).SaveAsync("FastDevicesSync", false, false);
        DependencyContainer.Log?.Error(DevicesService.ServiceName, `EXIT CONFIGURATION MODE WILL BE FIRED! DisableFastStatesSyncAsync()`);
        await Api.Get<IControllerConfigurationService>(ControllerConfigurationService.ServiceName).ExitConfigurationModeAsync();
        return res;
    }

    async IsFastStatesSyncEnabledAsync(): Promise<boolean | Status> {
        const res = await Api.Get<IRemoteStorageService>(RemoteStorageService.ServiceName).ReadAsync("FastDevicesSync", false, false);

        if (res instanceof RemoteStorageDataEntry) {
            return (res as RemoteStorageDataEntry<boolean>).Data;
        }

        //Controller returns ResourceDoesNotExists if fastSync option was never enabled (no option in DB - default slow sync)
        if ((res as Status) == Status.ResourceDoesNotExists)
            return false;

        return res;
    }

    GetDevicesForManualPairingAsync(protocolExtensionGuids?: string[] | undefined): Promise<ManuallyPairedDevice[]>;
    GetDevicesForManualPairingAsync(protocolExtensionGuid?: string | undefined): Promise<ManuallyPairedDevice[]>;
    async GetDevicesForManualPairingAsync(protocol?: unknown): Promise<ManuallyPairedDevice[]> {
        let service = Api.Get<IManuallyPairedDevicesService>(ManuallyPairedDevicesService.ServiceName);
        const regGuid = new RegExp("^[{]?[0-9a-fA-F]{8}-([0-9a-fA-F]{4}-){3}[0-9a-fA-F]{12}[}]?$");

        if (Array.isArray(protocol)) {
            return await service.GetDevicesForManualPairingAsync(protocol as string[]);
        }
        else if (regGuid.test(protocol as string)) {
            return await service.GetDevicesForManualPairingAsync(protocol as string);
        }
        else {
            return await service.GetDevicesForManualPairingAsync();
        }
    }

    async AddManuallyPairedDevice(device: ManuallyPairedDevice): Promise<DeviceTaskExecutionResult> {
        let res = await Api.Get<IManuallyPairedDevicesService>(ManuallyPairedDevicesService.ServiceName).AddManuallyPairedDeviceAsync(device);
        DependencyContainer.Log?.Error(DevicesService.ServiceName, `EXIT CONFIGURATION MODE WILL BE FIRED! AddManuallyPairedDevice()`);
        await Api.Get<IControllerConfigurationService>(ControllerConfigurationService.ServiceName).ExitConfigurationModeAsync();
        return res;
    }

    public GetDeviceChannelStates(device: IDevice, channel: number): DeviceChannelStates[] {
        return this._devicesChannelsStates.firstOrDefault(a => a.DeviceGuid == device.Guid)?.ChannelsStates.filter(a => a.Channel == channel) ?? [];
    }

    public GetDeviceStates(device: IDevice): DeviceChannelStates[] {
        return this._devicesChannelsStates.firstOrDefault(a => a.DeviceGuid == device.Guid)?.ChannelsStates ?? [];
    }

    private PutDeviceStateOnList(device: IDevice, state: IDeviceState<IDeviceStateData>): void {
        let deviceStatesOnList = this._devicesChannelsStates.firstOrDefault(a => a.DeviceGuid == device.Guid);

        if (deviceStatesOnList == null) {
            let s = new DeviceChannelStates();
            s.Channel = (state.Data as IDeviceStateData).Channel;
            s.States = [state];
            deviceStatesOnList = { DeviceGuid: device.Guid, ChannelsStates: [s] };
            this._devicesChannelsStates.push(deviceStatesOnList);
        }
        else {
            let channel = deviceStatesOnList.ChannelsStates.firstOrDefault(a => a.Channel == (state.Data as IDeviceStateData).Channel);

            if (channel == null) {
                channel = new DeviceChannelStates();
                channel.Channel = (state.Data as IDeviceStateData).Channel;
                channel.States = [state];
                deviceStatesOnList.ChannelsStates.push(channel);
            }
            else {
                let currentState = channel.States.firstOrDefault(a => a.Type == state.Type);

                if (currentState != null) {
                    if (currentState.TypeAsEnum == DeviceResponseType.BlindErrorState) {
                        channel.States = channel.States.where(a => (a.Data as BlindErrorState).ErrorCode != (state.Data as BlindErrorState).ErrorCode).toArray();
                    }
                    else {
                        channel.States = channel.States.where(a => a.Type != state.Type).toArray();
                    }
                }

                channel.States.push(state);
            }
        }
    }

    async GetCurrentlyRunningTaksAsync() {
        const result = await this._connection?.SendAndWaitForResponseAsync<string[]>(new GetRunningDevicesTasksRequest(), 15000, false);

        if (result?.Status == Status.OK && result.Data != null) {
            this.ParseDeviceTaskInfo(result.Data);
        }
    }

    ParseDeviceTaskInfo(tasks: string[]) {
        const devicesTasks: DeviceTasksInfo[] = [];

        tasks.forEach(taskChannelPair => {
            const pair = taskChannelPair.split(";");
            const task = new DeviceTasksInfo();
            task.DeviceGuid = pair[0];
            task.Channel = Number(pair[1]);
            task.Status = TaskExecution.ExecutingTasks;
            devicesTasks.push(task);

            if (task.Channel === 0) {
                const device = this._devices.find(a => a.Guid == task.DeviceGuid);

                if (device !== undefined) {
                    device.Channels.forEach(channel => {
                        const task = new DeviceTasksInfo();
                        task.DeviceGuid = device.Guid;
                        task.Channel = channel.Number;
                        task.Status = TaskExecution.ExecutingTasks;
                        devicesTasks.push(task);
                    });
                }
            }
        });

        (this._onDevicesTasksChangedEvent as TypedEvent<DeviceTasksInfo[]>).Invoke(devicesTasks);
    }

    MapApiDeviceStateToDeviceState(device: IDevice, state: DeviceStateChangedObject): IDeviceState<IDeviceStateData> | null {
        const st = state.state as any as DeviceState;

        switch (state.DataType) {
            case "ChannelOnOff":
                return new ChannelOnOffDeviceState(state);

            case "DoorBell":
                return new DoorBellDeviceState(state);

            case "ReedPosition":
                return new ReedStateDeviceState(state);

            case "BlindPosition":
                return new BlindPositionDeviceState(state);

            case "ButtonState":
                return new RemoteButtonDeviceState(state);

            case "LightColor":
                return new LightColorDeviceState(state);

            case "LightTemperature":
                return new LightWarmthDeviceState(state);

            case "LightBrightness":
                return new LightBrightnessDeviceState(state);

            case "TemperatureSensor":
            case "TemperatureState":
            case "MeasuredTemperature":
                return new MeasuredTemperatureDeviceState(state);

            case "BatteryState":
                return new BatteryDeviceState(state);

            case "MeasuredBrightness":
            case "DeviceBrightnessState":
                return new MeasuredBrightnessDeviceState(state);

            case "EnergyMeasuredPower":
                break;

            case "EnergyMeasuredCurrent":
                break;

            case "EnergyMeasuredVoltage":
                break;

            case "IEnergy":
            case "Energy":
            case "IGetPowerMeasurements":
                return new MeasuredEnergyDeviceState(state);

            case "DistanceSensor":
                return new MeasuredDistanceDeviceState(state);

            case "MovementSensor":
                return new MovementSensorDeviceState(state);

            case "BlindsControlButton":
                return new BlindRemoteButtonDeviceState(state);

            case "FacadeControlButton":
                return new FacadeRemoteButtonDeviceState(state);

            case "SignalStrength":
                return new SignalStrenghtDeviceState(state);

            case "GatePosition":
                return new GatePositionDeviceState(state);

            case "GatewayPosition":
                return new GatewayPositionDeviceState(state);

            case "GateControllerHealth":
                return new GateControllerHealthDeviceState(state);

            case "IBlindCalibrationStatus":
            case "BlindCalibration":
                return new BlindCalibrationDeviceState(state);

            case "HumiditySensor":
            case "HumidityState":
                return new HumiditySensorDeviceState(state);

            case "VibrationSensor":
                return new VibrationSensorDeviceState(state);

            case "DigitalInputSensor":
                return new BinarySensorStateDeviceState(state);

            case "ConfigurationState":
                return new ConfigurationDeviceState(state);

            case "BlindOpenCloseTime":
            case "IBlindOpenCloseTime":
                return new BlindOpenCloseTimeDeviceState(state);

            case "IOvercurrentProtection":
            case "OvercurrentProtection":
                return new OvercurrentProtectionDeviceState(state);

            case "IOvercurrentProtectionThreshold":
            case "OvercurrentProtectionThreshold":
                return new OvercurrentProtectionThresholdDeviceState(state);

            case "FacadePosition":
            case "FacadeState":
                return new FacadePositionDeviceState(state);

            case "FacadeTypeState":
                return new FacadeTypeDeviceState(state);

            case "CurrentWindThreshold":
                return new CurrentWindThresholdDeviceState(state);

            case "CurrentLightThreshold":
                return new CurrentLightThresholdDeviceState(state);

            case "WindSpeed":
                return new WindSpeedDeviceState(state);

            case "FloodSensor":
                return new FloodSensorDeviceState(state);

            case "PressureState":
                return new PressureSensorDeviceState(state);

            case "ITamperProtection":
            case "TamperProtection":
                return new TamperProtectionDeviceState(state);

            case "ILightRGBW":
            case "LightRGBW":
                return new LightRGBWDeviceState(state);

            case "OnlineCamera":
            case "IOnlineCamera":
                return new OnlineCameraState(state);

            case "BlindError":
            case "IBlindError":
                (state.state as any).ErrorData = ParseObjToMap<string, string>((state.state as any).ErrorData);
                return new BlindErrorDeviceState(state);

            case "HallState":
                return new ChannelHallStateState(state);

            case "SceneExecuted":
                return new ChannelSceneExecutedState(state);

            case "Intercom":
                return new IntercomDeviceState(state);

            default:
                DependencyContainer.Log?.Debug(DevicesService.ServiceName, `Not supported device state ${state.DataType}`);
                break;
        }

        return null;
    }

    GetServiceName(): string {
        return DevicesService.ServiceName;
    }

    async SyncDevicesStatesAsync(forceSlow = false): Promise<boolean> {
        const result = await this._connection?.SendAndWaitForResponseAsync<any>(new GetDevicesStatesRequest(), 15000, false);

        if ((await this.IsFastStatesSyncEnabledAsync()) === true && !forceSlow)
            return result?.Status == Status.OK;
        else {
            let syncStatesRequest = new SyncDevicesStatesRequest();
            syncStatesRequest.Data = true;
            const result = await this._connection?.SendAndWaitForResponseAsync<any>(syncStatesRequest, 35000, false);
            return result?.Status == Status.OK;
        }
    }

    OnDevicesTasksExecutionChangeEvent(): ITypedEvent<DeviceTasksInfo[]> {
        return this._onDevicesTasksChangedEvent;
    }

    OnDeviceStateChangedEvent(): ITypedEvent<{ Device: IDevice, State: IDeviceState<IDeviceStateData> }> {
        return this._onDeviceStateChangedEvent;
    }

    OnDeviceStateRefreshedOrChangedEvent(): ITypedEvent<{ Device: IDevice; State: IDeviceState<IDeviceStateData>; }> {
        return this._onDeviceStateRefreshedOrChangedEvent;
    }

    OnDeviceRegisteredEvent(): ITypedEvent<IDevice> {
        return this._onDeviceRegisteredEvent;
    }

    OnDeviceRemovedEvent(): ITypedEvent<IDevice> {
        return this._onDeviceRemovedEvent;
    }

    OnDeviceFoundEvent(): ITypedEvent<IDevice> {
        return this._onDeviceFoundEvent;
    }

    private MapApiDevices(devicesObjects: DeviceObject[]): IDevice[] {
        const devices: IDevice[] = [];

        if (devicesObjects == null)
            return [];

        devicesObjects.forEach(element => {
            let device = new Device();
            device.Guid = element.Guid;
            device.Name = element.DeviceName;
            device.ChannelsAmount = element.ChannelsNumber;
            device.DeviceType = element.DeviceType;
            device.CommunicationWay = element.CommunicationWay;
            device.DeviceState = element.DeviceState;
            device.IsEnabled = element.IsEnabled;
            device.IsVirtual = element.IsVirtual;
            device.SerialNumber = element.DeviceSerialNumber;
            device.ManufacturerGuid = element.ManufacturerGuid;
            device.ModelGuid = element.DeviceModelGuid;
            device.Model = element.DeviceModel;
            device.Name = element.DeviceName;
            device.ProtocolGuid = element.ProtocolGuid;

            if (element.IconType !== undefined && element.IconType !== null)
                device.IconType = element.IconType;
            else
                device.IconType = IconType.Unknown;

            try {
                element.AvailableTasks?.forEach(task => {
                    let typeInfo = new DeviceTaskTypeInfo();

                    if (device.CommunicationWay == CommunicationWay.OneWay) {
                        switch (task) {
                            case "IBlindPosition":
                                typeInfo.InterfaceType = "IBlindPositionSimple";
                                break;
                        }
                    }
                    else
                        switch (task) {
                            case "IGatePosition":
                                typeInfo.InterfaceType = "IGatePulse";
                                break;

                            case "IGatewayPosition":
                                typeInfo.InterfaceType = "IGatewayPulse";
                                break;

                            default:
                                typeInfo.InterfaceType = task;
                                break;
                        }

                    device.AvailableTaskTypes.push(typeInfo);
                });

                element.AvailableResponses?.forEach(response => {
                    let typeInfo = new DeviceResponseTypeInfo();
                    typeInfo.InterfaceType = response;
                    device.AvailableResponseTypes.push(typeInfo);
                });

                element.ChannelsConfiguration?.forEach(configuration => {
                    try {
                        let ch = new DeviceChannel();
                        ch.Number = configuration.Channel;
                        device.Channels.push(ch);
                        ch.SetDevice(device);
                        ch.Name = configuration.ChannelName;
                        ch.ChannelGroups = configuration.GroupsGuids;
                        ch.IconName = configuration.IconName;
                        ch.IsHidden = configuration.Hidden;
                        ch.Configurations = configuration.Configurations;
                        ch.CustomDataAndRolesSupported = configuration.CustomData !== undefined && configuration.CustomData !== null;

                        if (configuration.CustomData !== undefined && configuration.CustomData !== null)
                            ch.CustomData = configuration.CustomData;

                        if (configuration.Roles !== undefined && configuration.Roles !== null)
                            ch.Roles = configuration.Roles;

                        configuration.AvailableResponses?.forEach(response => {
                            let typeInfo = new DeviceResponseTypeInfo();
                            typeInfo.InterfaceType = response;
                            ch.AvailableResponseTypes.push(typeInfo);
                        });

                        configuration.AvailableTasks?.forEach(task => {
                            let typeInfo = new DeviceTaskTypeInfo();

                            if (device.AvailableTaskTypes.any(a => a.InterfaceType == "IBlindPositionSimple")) {
                                if (task == "IBlindPosition")
                                    typeInfo.InterfaceType = "IBlindPositionSimple";
                            }
                            else {
                                typeInfo.InterfaceType = task;
                            }
                            ch.AvailableTaskTypes.push(typeInfo);
                        });
                    }
                    catch (ex: any) {
                        DependencyContainer.Log?.Error(ex);
                    }
                });

                if (device.ModelGuid === "f4cc5043-2198-47df-9361-4cd130ed0323") {
                    // when confuiguration is missing, we add measured energy to first channel
                    if (device.Channels.first().AvailableTaskTypes.all(a => a.Type != DeviceTaskType.GetPowerMeasurements)) {
                        let typeInfo = new DeviceTaskTypeInfo();
                        typeInfo.InterfaceType = "IGetPowerMeasurements";
                        device.Channels.first().AvailableTaskTypes.push(typeInfo);
                    }
                }
            }
            catch (ex: any) {
                DependencyContainer.Log?.Error(ex);
            }
            devices.push(device);
        });

        return devices;
    }

    private async GetPairedDevicesAsync(withScenes: boolean = false): Promise<IDevice[]> {
        let conf = await this._controllerConfiguration?.DidCofigurationChangeAsync();
        if (this._synchronized && !conf) {
            if (withScenes)
                return Promise.resolve(this._devices);
            else
                return Promise.resolve(this._devices.filter(d => d.DeviceType != DeviceType.Scene));
        }

        const result = await this._connection?.SendAndWaitForResponseAsync<DeviceObject[]>(new GetDevicesListRequest(), 15000, true);

        if (result == null || result === undefined)
            return [];

        if (result.Status == Status.OK && result.Data != null) {
            this._devices = this.MapApiDevices(result.Data);

            if (withScenes)
                return this._devices;
            else
                return this._devices.filter(d => d.DeviceType != DeviceType.Scene);
        }
        else {
            DependencyContainer.Log?.Error(DevicesService.ServiceName, `Failed to get devices. ${result.Status} ${result.Data}`);
            return [];
        }
    }

    async GetDevicesAsync(withScenes: boolean = false): Promise<IDevice[]> {
        await this.WaitForSynchronizationAsync();
        return this.GetPairedDevicesAsync(withScenes);
    }

    async GetFoundDevicesAsync(): Promise<IDevice[]> {
        const result = await this._connection?.SendAndWaitForResponseAsync<DeviceObject[]>(new GetFoundDevicesRequest(), 15000, false);

        if (result == null)
            return [];

        if (result.Status == Status.OK && result.Data != null)
            return this.MapApiDevices(result.Data).where(a => a.DeviceType !== DeviceType.Scene).toArray();
        else {
            DependencyContainer.Log?.Error(DevicesService.ServiceName, `Failed to get found devices. ${result.Status} ${result.Data}`);
            return [];
        }
    }

    GetDevice(guid: string): IDevice | undefined {
        return this._devices.find(a => a.Guid === guid);
    }

    async FindDevicesAsync(): Promise<void> {
        Api.Get<IWebApiCacheService>(WebApiCacheService.ServiceName).ClearCache();
        await this._connection?.SendAndWaitForResponseAsync<any>(new SearchForDevicesRequest(), 35000, false);
    }

    async StopSearchingForDevices(): Promise<void> {
        await this._connection?.SendAndWaitForResponseAsync<any>(new StopSearchingForDevicesRequest(), 20000, false);
    }

    async RegisterDeviceAsync(device: IDevice): Promise<DeviceTaskExecutionResult> {
        try {
            const req = new DevicePairRequest();
            req.Data = device.Guid;
            const res = await this._connection?.SendAndWaitForResponseAsync<any>(req, 20000, false);
            DependencyContainer.Log?.Error(DevicesService.ServiceName, `EXIT CONFIGURATION MODE WILL BE FIRED! RegisterDeviceAsync()`);
            Api.Get<IControllerConfigurationService>(ControllerConfigurationService.ServiceName).ExitConfigurationModeAsync();

            if (res?.Status == Status.OK) {
                if (!(this._devices.any(d => d.Guid == device.Guid)))
                    this._devices.push(device);

                (this._onDeviceRegisteredEvent as TypedEvent<IDevice>).Invoke(device);
                return DeviceTaskExecutionResult.Executed;
            }
            else
                return DeviceTaskExecutionResult.Failed;
        } catch (ex) {
            DependencyContainer.Log?.Error(DevicesService.ServiceName, `Failed to paired devices: ${ex}`);
            return DeviceTaskExecutionResult.ControllerResponseTimeout;
        }
    }

    async RemoveDeviceAsync(device: IDevice, force: boolean = false): Promise<DeviceTaskExecutionResult> {
        try {
            let req = new DeviceUnpairRequest();

            if (force)
                req = new ForceDeviceUnpairRequest();

            req.Data = device.Guid;
            const res = await this._connection?.SendAndWaitForResponseAsync<any>(req, 35000, false);
            DependencyContainer.Log?.Error(DevicesService.ServiceName, `EXIT CONFIGURATION MODE WILL BE FIRED! RemoveDeviceAsync()`);
            Api.Get<IControllerConfigurationService>(ControllerConfigurationService.ServiceName).ExitConfigurationModeAsync();

            if (res?.Status == Status.OK) {
                this._devices = this._devices.where(a => a.Guid != device.Guid).toArray();
                (this._onDeviceRemovedEvent as TypedEvent<IDevice>).Invoke(device);
                return DeviceTaskExecutionResult.Executed;
            }
            else
                return DeviceTaskExecutionResult.Failed;
        } catch (ex) {
            DependencyContainer.Log?.Error(DevicesService.ServiceName, `Failed to unpair device: ${ex}`);
            return DeviceTaskExecutionResult.ControllerResponseTimeout;
        }
    }

    private MapDeviceTaskToDeviceTaskInfo(device: IDevice, task: IDeviceTask): DeviceTaskInfo {
        let t = new DeviceTaskInfo();
        const tsk = task as DeviceTask;

        switch (tsk.FeatureType) {
            case DeviceControlFeature.SetTemperature:
                t.Data = (tsk as SetTemperature).Temperature;
                break;

            case DeviceControlFeature.SetLightBrightness:
                t.Data = (tsk as SetLightBrightness).Brightness;
                break;

            case DeviceControlFeature.SetLightColor:
                t = new LightColorTaskInfo();
                const ts = tsk as SetLightColor;
                (t as LightColorTaskInfo).R = ts.R;
                (t as LightColorTaskInfo).G = ts.G;
                (t as LightColorTaskInfo).B = ts.B;
                break;

            case DeviceControlFeature.SetLightTemperature:
                t.Data = (tsk as SetLightTemperature).Temperature;
                break;

            case DeviceControlFeature.SetGatePositionPrecise:
                switch ((tsk as SetGatePositionPrecise).GateControlAction) {
                    case GateActionEnum.Open:
                        t.Data = 101;
                        break;

                    case GateActionEnum.Close:
                        t.Data = 102;
                        break;

                    case GateActionEnum.Stop:
                        t.Data = 103;
                        break;

                    case GateActionEnum.Percentage:
                        t.Data = (tsk as SetGatePositionPrecise).Position;
                        break;
                }
                break;

            case DeviceControlFeature.SetGatePositionPreciseSecure:
                let position = 0;
                let pin = (tsk as SetGatePositionPreciseSecure).Pin;

                switch ((tsk as SetGatePositionPreciseSecure).GateControlAction) {
                    case GateActionEnum.Open:
                        position = 101;
                        break;

                    case GateActionEnum.Close:
                        position = 102;
                        break;

                    case GateActionEnum.Stop:
                        position = 103;
                        break;

                    case GateActionEnum.Percentage:
                        position = (tsk as SetGatePositionPreciseSecure).Position;
                        break;
                }
                t.Data = {
                    Position: position,
                    Pin: pin
                }
                break;

            case DeviceControlFeature.SetBlindPosition:
                switch ((tsk as SetBlindPosition).Action) {
                    case BlindActionEnum.Open:
                        t.Data = 101;
                        break;

                    case BlindActionEnum.Close:
                        t.Data = 102;
                        break;

                    case BlindActionEnum.Stop:
                        t.Data = 103;
                        break;

                    case BlindActionEnum.Percentage:
                        t.Data = (tsk as SetBlindPosition).Position;
                        break;
                }
                break;

            case DeviceControlFeature.SetLightBrightnessDynamicly:
                t.Data = (tsk as SetLightBrightnessDynamicly).Brightness;
                break;

            case DeviceControlFeature.SetBlindOpenCloseTime:
                t.Data = (tsk as SetBlindOpenCloseTime).Data;
                break;

            case DeviceControlFeature.SetBlindMicroventilation:
                t.Data = (tsk as SetBlindMicroventilation).Position;
                break;

            case DeviceControlFeature.SetFacadePositionAndTilt:
                t = new FacadePositionTaskInfo();
                const fp = tsk as SetFacadePosition;
                (t as FacadePositionTaskInfo).FacadeAction = fp.FacadeAction;
                (t as FacadePositionTaskInfo).Tilt = fp.Tilt;
                (t as FacadePositionTaskInfo).Position = fp.Position;
                break;
            case DeviceControlFeature.LightRGBW:
                t.Data = (tsk as LightRGBW).Data;
                break;
            case DeviceControlFeature.GatePulse:
            case DeviceControlFeature.GatewayPulse:
                t.Data = 0;
                break;
            case DeviceControlFeature.TurnOnWithTimeout:
                t.Data = (tsk as TurnOnWithTime).SwitchOffDelaySeconds;
                break;
        }

        t.Channel = tsk.Channel;
        t.DeviceGuid = device.Guid;
        t.ControlFeature = tsk.FeatureType;
        return t;
    }

    async ExecuteDeviceTaskAsync(device: IDevice, task: IDeviceTask): Promise<DeviceTaskExecutionResult> {
        try {
            DependencyContainer.Log?.Debug(DevicesService.ServiceName, `Preparing device task execution, device: ${device.Guid} task: ${task.TaskType} channel: ${task.Channel}`);
            let t = this.MapDeviceTaskToDeviceTaskInfo(device, task);

            try {
                const req = new ExecuteDeviceTaskRequest(t);
                DependencyContainer.Log?.Debug(DevicesService.ServiceName, `Sending device task for execution, device: ${t.DeviceGuid} task: ${t.ControlFeature} channel: ${t.Channel} request: ${JSON.stringify(req)}`);
                const result = await this._connection?.SendAndWaitForResponseAsync<any>(req, 15000, false);

                switch (result?.Status) {
                    case Status.OK:
                        return DeviceTaskExecutionResult.Executed;

                    case Status.FatalError:
                        DependencyContainer.Log?.Error(DevicesService.ServiceName, `Fatal error occured when tried to execute task on device. Error code: ${result.Data}`);
                        return DeviceTaskExecutionResult.Failed;

                    case Status.WrongData:
                        switch (result.Data) {
                            case "DeviceNotFound":
                                return DeviceTaskExecutionResult.DeviceNotFound;
                        }
                        break;

                    case Status.Error:
                        switch (result.Data) {
                            case "DeviceResponseTimeout":
                                return DeviceTaskExecutionResult.DeviceResponseTimeout;
                        }
                        DependencyContainer.Log?.Error(DevicesService.ServiceName, `Unknown error occured when trying to execute task on device. Error code: ${result.Data}`);
                        return DeviceTaskExecutionResult.Failed;
                }
            }
            catch (ex: any) {
                if ((ex as Error).message.includes("timeout")) {
                    DependencyContainer.Log?.Error(DevicesService.ServiceName, `Failed to get controller response in time.\n${ex}`);
                    return DeviceTaskExecutionResult.ControllerResponseTimeout;
                }
                else {
                    DependencyContainer.Log?.Error(DevicesService.ServiceName, `Unknown error occured when tried to execute task on device.\n${ex}`);
                    return DeviceTaskExecutionResult.Failed;
                }
            }

            return DeviceTaskExecutionResult.Unknown;
        }
        catch (ex: any) {
            DependencyContainer.Log?.Error(DevicesService.ServiceName, `Unknown error occured when tried to execute task on device.\n${ex}`);
            return DeviceTaskExecutionResult.Failed;
        }
    }

    async ExecuteDevicesTasksAsync(tasks: [Device: IDevice, Task: IDeviceTask][]): Promise<DeviceTaskPairExecutionResult[]> {
        let results: DeviceTaskPairExecutionResult[] = [];
        let tasksPair: DeviceTaskInfo[] = [];
        let devices: IDevice[] = [];
        let position = 0;

        for (const [device, task] of tasks) {
            const tmp = this.MapDeviceTaskToDeviceTaskInfo(device, task);
            tmp.SequnceExecutionOrder = position;
            position++;
            tasksPair.push(tmp);
        }

        const req = new ExecuteDevicesTasksRequest(tasksPair);
        try {
            const result = await this._connection?.SendAndWaitForResponseAsync<DevicesTaskExecutionApiResult[]>(req, 120000, false);

            let responsePosition = -1;
            result?.Data?.forEach(element => {
                responsePosition++;
                let res: DeviceTaskExecutionResult = DeviceTaskExecutionResult.Unknown;

                switch (element.Status) {
                    case Status.OK:
                        res = DeviceTaskExecutionResult.Executed;
                        break;

                    case Status.FatalError:
                        DependencyContainer.Log?.Error(DevicesService.ServiceName, `Fatal error occured when tried to execute task on device. Error code: ${element.Data}`);
                        res = DeviceTaskExecutionResult.Failed;
                        break;

                    case Status.WrongData:
                        switch (element.Data) {
                            case "DeviceNotFound":
                                res = DeviceTaskExecutionResult.DeviceNotFound;
                                break;
                        }
                        break;

                    case Status.Error:
                        switch (element.Data) {
                            case "DeviceResponseTimeout":
                                res = DeviceTaskExecutionResult.DeviceResponseTimeout;
                                break;

                            default:
                                DependencyContainer.Log?.Error(DevicesService.ServiceName, `Unknown error occured when trying to execute task on device. Error code: ${result.Data}`);
                                res = DeviceTaskExecutionResult.Failed;
                                break;
                        }
                        break;
                }

                let pos = 0;
                let info = new DeviceTaskPairExecutionResult();

                for (const [device, task] of tasks) {
                    if (pos == responsePosition) {
                        info.Device = device;
                        info.Task = task;
                        info.Channel = task.Channel;
                        info.Result = res;
                        results.push(info);
                        break;
                    }
                    else
                        pos++;
                }
            });
        }
        catch (ex: any) {
            if ((ex as Error).message.includes("timeout")) {
                DependencyContainer.Log?.Error(DevicesService.ServiceName, `Failed to get controller response in time.\n${ex}`);

                for (const [device, task] of tasks) {
                    let info = new DeviceTaskPairExecutionResult();
                    results.push(info);
                    info.Device = device;
                    info.Task = task;
                    info.Channel = task.Channel;
                    info.Result = DeviceTaskExecutionResult.ControllerResponseTimeout;
                }
            }
            else {
                DependencyContainer.Log?.Error(DevicesService.ServiceName, `Unknown error occured when tried to execute task on device.\n${ex}`);

                for (const [device, task] of tasks) {
                    let info = new DeviceTaskPairExecutionResult();
                    results.push(info);
                    info.Device = device;
                    info.Task = task;
                    info.Channel = task.Channel;
                    info.Result = DeviceTaskExecutionResult.Failed;
                }
            }
        }
        return results;
    }
}

class DeviceChannelStates {
    Channel: number = 0;
    States: IDeviceState<IDeviceStateData>[] = [];
}

class DeviceObject {
    Guid: string = "";
    CloudGuid: string = "";
    DeviceSerialNumber: string = "";
    DeviceModelGuid: string = "";
    ProtocolGuid: string = "";
    IconType: IconType = IconType.Unknown;
    DeviceName: string = "";
    DeviceModel: string = "";
    ManufacturerGuid: string = "";
    ChannelsNumber: number = 0;
    IsVirtual: boolean = false;
    IsEnabled: boolean = false;
    DeviceState: DeviceState = DeviceState.Working;
    CommunicationWay: CommunicationWay = CommunicationWay.OneWay;
    DeviceType: DeviceType = DeviceType.Unknown;
    ChannelsConfiguration: ChannelConfigurationObject[] = [];
    Groups: DeviceGroupObject[] = [];
    AvailableTasks: string[] = [];
    AvailableResponses: string[] = [];
}

class DeviceGroupObject {
    Guid: string = "";
    Name: string = "";
}

class ChannelConfigurationObject {
    Channel: number = 0;
    ChannelName: string = "";
    Configuration: ChannelConfigurations = ChannelConfigurations.Default;
    Configurations: Record<string, string> = {};
    CustomData: Record<string, string> = {};
    Roles: Roles[] = [];
    IconName: string = "";
    GroupsGuids: string[] = [];
    Hidden: boolean = false;
    AvailableTasks: any[] = [];
    AvailableResponses: any[] = [];
}

class GetDevicesListRequest extends DataFrame<any> {
    constructor() {
        super();
        this.Resource = "/devices/list";
        this.Method = Method.Get;
    }
}

class GetDevicesStatesRequest extends DataFrame<any> {
    constructor() {
        super();
        this.Resource = "/devices/channels/states";
        this.Method = Method.Get;
    }
    Data: boolean = false;
}

class SyncDevicesStatesRequest extends DataFrame<any> {
    constructor() {
        super();
        this.Resource = "/devices/channels/states/sync";
        this.Method = Method.Get;
    }
    Data: boolean = false;
}

class SearchForDevicesRequest extends DataFrame<any> {
    constructor() {
        super();
        this.Resource = "/devices/search";
        this.Method = Method.Post;
    }
}

class StopSearchingForDevicesRequest extends DataFrame<any> {
    constructor() {
        super();
        this.Resource = "/devices/search/stop";
        this.Method = Method.Post;
    }
}

class GetFoundDevicesRequest extends DataFrame<any> {
    constructor() {
        super();
        this.Resource = "/devices/found/list";
        this.Method = Method.Get;
    }
}

/**
 * @type Class
 */
export class DeviceStateChangedObject {
    DeviceGuid: string = "";
    state: DeviceStateObject = new DeviceStateObject();
    Type: number = 0;
    DataType: string = "";
}

/**
 * @type Class
 */
export class DeviceStateObject {
    Channel: number = 0;
    StateReliability: StateReliability = StateReliability.Unconfident;
}

class GetRunningDevicesTasksRequest extends DataFrame<any> {
    constructor() {
        super();
        this.Resource = "/devices/tasks";
        this.Method = Method.Get;
    }
}

class DevicePairRequest extends DataFrame<string> {
    constructor() {
        super();
        this.Resource = "/devices/device/pair";
        this.Method = Method.Put;
    }
}

class DeviceUnpairRequest extends DataFrame<string> {
    constructor() {
        super();
        this.Resource = "/devices/device/unpair";
        this.Method = Method.Put;
    }
}

class ForceDeviceUnpairRequest extends DataFrame<string> {
    constructor() {
        super();
        this.Resource = "/devices/device/unpair/force";
        this.Method = Method.Put;
    }
}

class ExecuteDeviceTaskRequest extends DataFrame<DeviceTaskInfo> {
    constructor(task: DeviceTaskInfo) {
        super();
        this.Resource = "/devices/device/control";
        this.Method = Method.Post;
        this.Data = task;
    }
}

class ExecuteDevicesTasksRequest extends DataFrame<DeviceTaskInfo[]> {
    constructor(tasks: DeviceTaskInfo[]) {
        super();
        this.Resource = "/devices/devices/control";
        this.Method = Method.Post;
        this.Data = tasks;
    }
}

class DeviceIsUsedInScenesRequest extends DataFrame<string> {
    constructor(guid: string) {
        super();
        this.Resource = "/logic/device/used";
        this.Method = Method.Get;
        this.Data = guid;
    }
}

class DeviceTaskInfo {
    DeviceGuid: string = "";
    Channel: number = 0;
    ControlFeature: DeviceControlFeature = DeviceControlFeature.GetState;
    Data: any;
    /// does't exist in controller, its is used only by this library to map responses that are returned in order to tasks
    SequnceExecutionOrder: number = 0;
}

class LightColorTaskInfo extends DeviceTaskInfo {
    R: number = 0;
    G: number = 0;
    B: number = 0;
}

class LightRGBWTaskInfo extends DeviceTaskInfo {
    R: number = 0;
    G: number = 0;
    B: number = 0;
    W: number = 0;
    Brightness: number = 0;
}

class FacadePositionTaskInfo extends DeviceTaskInfo {
    FacadeAction: FacadeActionEnum = FacadeActionEnum.Stop;
    Tilt: number = 0;
    Position: number = 0;
}

class DevicesTaskExecutionApiResult {
    DeviceGuid: string = "";
    Data: any;
    Status: Status = Status.OK;
}

class DeviceUsedInScenesResponse {
    DeviceGuid: string = "";
    ConditionsAsCondition: string[] = [];
    ConditionsAsTask: string[] = [];
}

/**
 * @type Class
 */
export class DeviceUsedInScenes {
    DeviceGuid: string = "";
    ConditionsAsCondition: ISequenceInfo[] = [];
    ConditionsAsTask: ISequenceInfo[] = [];
}
